import {
  Dialog,
  DialogContent,
  DialogTitle,
  Select,
  IconButton,
  Grid,
  MenuItem,
  Typography,
  Paper,
  Button,
  DialogActions,
} from "@mui/material";
import { DialogProps } from "@toolpad/core";
import { ReactNode, useState } from "react";
import {
  Control,
  UseFormReset,
  Controller,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { Flex } from "~/components/Flex";
import { PairingIcon } from "~/icons";
import { alpha } from "@mui/material/styles";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { Close as CloseIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { GithubSecurityPipelineFormInput } from "~/pages/integrations/security-pipeline/security-pipeline-github/types";
import { pluralize } from "~/lib/pluralize";
import { SelectOptions } from "~/types/form";

interface GithubSecurityPipelineAssignSpacesDialogPayload {
  control: Control<GithubSecurityPipelineFormInput>;
  reset: UseFormReset<GithubSecurityPipelineFormInput>;
  scope: SpaceScope | OrganizationScope;
  repositoriesOptions: SelectOptions<string>;
  spaceOptions: SelectOptions<string>;
  triggerSubmit: () => void;
}

export const GithubSecurityPipelineAssignSpacesDialog = ({
  onClose,
  open,
  payload,
}: DialogProps<GithubSecurityPipelineAssignSpacesDialogPayload>) => {
  const [isLoading, setIsLoading] = useState(false);
  const { control, repositoriesOptions, spaceOptions, reset, triggerSubmit } =
    payload;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "assignedSpaces",
  });

  const watchAssignedSpaces = useWatch({ control, name: "assignedSpaces" });

  const availableRepositoriesOptions = repositoriesOptions.filter(
    (repo) =>
      !watchAssignedSpaces.find(
        (selectedAssignedSpaces) =>
          repo.value === selectedAssignedSpaces.repositoryLink,
      ),
  );

  const availableSpacesOptions = spaceOptions.filter(
    (space) =>
      !watchAssignedSpaces.find(
        (selectedAssignedSpaces) =>
          selectedAssignedSpaces.spaceMrn === space.value,
      ),
  );

  const handleClose = () => {
    reset();
    onClose();
    setIsLoading(false);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={handleClose}
      slotProps={{ paper: { sx: { maxHeight: "568px" } } }}
    >
      <IconButton
        aria-label="close pairing"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 24,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle
        variant="h5"
        sx={{
          alignSelf: "stretch",
          textAlign: "center",
          color: "text.light",
          fontWeight: 700,
          textTransform: "uppercase",
          display: "flex",
          flexDirection: "column",
          p: 5,
          pb: 3,
        }}
      >
        Space Repo pairing
        <Typography
          color="text.secondary"
          variant="body2"
          fontSize={16}
          sx={{ textTransform: "none" }}
        >
          Mapping a space to a security repo will allow you to run automated
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 5 }}>
        <Paper
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            p: 2,
            borderRadius: 1,
            minHeight: "340px",
            boxShadow: "none",
            ...(fields.length === 0 && {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }),
          }}
        >
          <Grid size={12} container rowSpacing={1}>
            {fields.length === 0 ? (
              <Grid
                size={12}
                gap={1}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center" }}
                >
                  Add a new space repo pairing to get started
                </Typography>
                <Button
                  sx={{ color: "text.secondary" }}
                  variant="outlined"
                  disabled={
                    availableSpacesOptions.length === 0 ||
                    availableRepositoriesOptions.length === 0
                  }
                  onClick={() => append({ spaceMrn: "", repositoryLink: "" })}
                >
                  Add New Pair
                </Button>
              </Grid>
            ) : (
              <>
                <Typography color="text.light" fontSize={16}>
                  {fields.length} {pluralize("space", fields.length)} protected
                </Typography>
                <Grid container size={12} columnSpacing={1}>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">
                      Space
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="caption" color="text.secondary">
                      Security Fix Repo
                    </Typography>
                  </Grid>
                </Grid>
                {fields.map((_, i) => (
                  <Grid container size={12} columnSpacing={1} key={_.id}>
                    <Grid size={6} columnSpacing={1}>
                      <Flex alignItems="center" gap={1}>
                        <Controller
                          name={`assignedSpaces.${i}.spaceMrn`}
                          {...{ control }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              value={
                                spaceOptions.find(
                                  (space) => space.value === field.value,
                                )?.value || ""
                              }
                              onChange={(e) => {
                                field.onChange(e.target.value);
                              }}
                              fullWidth
                              sx={{
                                "&.MuiInputBase-root": {
                                  bgcolor: "code.background",
                                  minWidth: 0,
                                },
                              }}
                              displayEmpty
                              renderValue={(selected: ReactNode) => {
                                const selectedSpace = spaceOptions?.find(
                                  (space) => space.value === selected,
                                );

                                const selectedSpaceTitle = selectedSpace?.label;

                                if (!selectedSpaceTitle) {
                                  return <>Select a space</>;
                                }

                                if (spaceOptions.length === 0) {
                                  return (
                                    <>No spaces available – create one first</>
                                  );
                                }

                                if (!selected || !selectedSpaceTitle) {
                                  return <>Select a space</>;
                                }

                                return <>{selectedSpaceTitle}</>;
                              }}
                            >
                              {(availableSpacesOptions || []).map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                        <PairingIcon sx={{ color: "text.disabled" }} />
                      </Flex>
                    </Grid>
                    <Grid size={6}>
                      <Flex alignItems="center">
                        <Controller
                          name={`assignedSpaces.${i}.repositoryLink`}
                          {...{ control }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              fullWidth
                              sx={{
                                "&.MuiInputBase-root": {
                                  bgcolor: "code.background",
                                  minWidth: 0,
                                },
                              }}
                              displayEmpty
                              renderValue={(selected: ReactNode) => {
                                const selectedRepository =
                                  repositoriesOptions?.find(
                                    (repository) =>
                                      repository.value === selected,
                                  );

                                const selectedRepositoryTitle =
                                  selectedRepository?.label;

                                if (!selectedRepositoryTitle) {
                                  return <>Select a repository</>;
                                }

                                if (selectedRepositoryTitle.length === 0) {
                                  return (
                                    <>
                                      No repositories available – create one
                                      first
                                    </>
                                  );
                                }

                                if (!selected || !selectedRepositoryTitle) {
                                  return <>Select a repository</>;
                                }

                                return <>{selectedRepositoryTitle}</>;
                              }}
                            >
                              {(availableRepositoriesOptions || []).map(
                                (option) => (
                                  <MenuItem
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ),
                              )}
                            </Select>
                          )}
                        />
                        {fields.length >= 1 && (
                          <IconButton
                            size="large"
                            sx={{
                              mx: 0.5,
                              color: (theme) => theme.palette.error.main,
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  alpha(theme.palette.error.main, 0.15),
                              },
                            }}
                            onClick={() => remove(i)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Flex>
                    </Grid>
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions sx={{ p: 5, pt: 3, gap: 1 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => append({ spaceMrn: "", repositoryLink: "" })}
          sx={{
            minWidth: "40px",
            height: "40px",
          }}
          disabled={
            availableSpacesOptions.length === 0 ||
            availableRepositoriesOptions.length === 0
          }
        >
          Add New Pair
        </Button>
        <Button
          loading={isLoading}
          onClick={async () => {
            setIsLoading(true);
            await triggerSubmit();
            setIsLoading(false);
            onClose();
          }}
          variant="contained"
          color="primary"
          sx={{
            minWidth: "40px",
            height: "40px",
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
